import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, MessageSquare, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';
import { useAIAgentChat } from '@/contexts/ai-agents';
import { useManualCacheRefresh } from '@/hooks/navigation/useProjectsPagesDatabaseCache';

const MobileAIAgentButton: React.FC = () => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { 
    isModalOpen,
    openModal,
    closeModal,
    toggleModal 
  } = useLocalStorageState();
  
  const { currentAssistant } = useAIAgentChat();
  const { refreshCache } = useManualCacheRefresh();
  
  // ✅ CLEAN: Enhanced click handler with localStorage and cache refresh
  const handleClick = async () => {
    setIsAnimating(true);
    setIsLoading(true);
    
    try {
      if (isModalOpen) {
        // If chat is open, close it
        closeModal();
      } else {
        // If chat is closed, open it and refresh cache
        openModal();
        
        // 🔄 CACHE REFRESH: Update AI context when opening modal (non-blocking)
        try {
          console.log('[MobileAIAgentButton] Refreshing AI context cache...');
          await refreshCache();
          console.log('[MobileAIAgentButton] AI context cache refreshed successfully');
        } catch (cacheError) {
          // Cache refresh failure shouldn't block UI - log and continue
          console.warn('[MobileAIAgentButton] Cache refresh failed (non-blocking):', cacheError);
        }
      }
    } catch (error) {
      console.error('Failed to handle mobile chat action:', error);
    } finally {
      setIsLoading(false);
      // Reset animation after delay
      setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
  };

  // Show loading state if loading or animating
  const showLoading = isAnimating || isLoading;
  
  // Simplified logic: Always show MessageSquare when not loading
  const showCompass = showLoading;
  const showMessageSquare = !showLoading;

  return (
    <motion.button
      onClick={handleClick}
      disabled={isLoading}
      className={cn(
        "group relative flex flex-col items-center justify-center text-left transition-all duration-300 ease-in-out",
        "w-16 h-12 min-h-[48px] rounded-md overflow-hidden sm:w-20 sm:h-auto sm:min-h-[48px] sm:px-3 sm:py-2 sm:rounded-none",
        "text-slate-700 dark:text-slate-200",
        "border border-white/30 dark:border-white/10 hover:border-white/50 dark:hover:border-white/20",
        // Active state when modal is open
        isModalOpen && "border-blue-400/50 dark:border-blue-500/50",
        // Disabled state
        isLoading && "opacity-50 cursor-not-allowed"
      )}
      whileHover={!isLoading ? { 
        scale: 1.02,
        transition: { duration: 0.3, ease: "easeInOut" }
      } : {}}
      whileTap={!isLoading ? { 
        scale: 0.98,
        transition: { duration: 0.2 }
      } : {}}
    >
      {/* Background gradient - always consistent */}
      <div className="absolute inset-0 w-full h-full">
        <div className={cn(
          "w-full h-full bg-gradient-to-br",
          isModalOpen 
            ? "from-blue-100/90 via-purple-100/80 to-blue-100/90 dark:from-blue-900/30 dark:via-purple-900/40 dark:to-blue-900/30"
            : "from-purple-100/80 via-blue-100/70 to-purple-100/80 dark:from-blue-900/20 dark:via-purple-900/30 dark:to-blue-900/20"
        )} />
      </div>

      {/* Overlay effects - on top of background */}
      <div className={cn(
        "absolute inset-0 bg-gradient-to-br opacity-40 dark:opacity-30 pointer-events-none",
        isModalOpen 
          ? "from-blue-100/60 via-purple-100/50 to-blue-100/60 dark:from-blue-900/20 dark:via-purple-900/30 dark:to-blue-900/20"
          : "from-purple-100/50 via-blue-100/40 to-purple-100/50 dark:from-blue-900/10 dark:via-purple-900/20 dark:to-blue-900/10"
      )} />
      
      {/* Elegant flowing shimmer effect - on top */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full animate-[shimmer_3s_infinite_ease-in-out] pointer-events-none" />
      
      {/* Icon container - loading or MessageSquare */}
      <div className={cn(
        'relative z-10 flex items-center justify-center transition-transform duration-300',
        'w-8 h-8',
        isModalOpen 
          ? 'text-blue-600 dark:text-blue-300 scale-110'
          : 'text-blue-500 dark:text-blue-300 group-hover:text-blue-600 dark:group-hover:text-blue-200 group-hover:scale-110'
      )}>
        <AnimatePresence mode="wait">
          {showCompass ? (
            <motion.div
              key="compass"
              initial={{ rotateY: -90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: 90, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="flex items-center justify-center"
            >
              <Compass size={20} strokeWidth={2} className="animate-spin" />
            </motion.div>
          ) : (
            <motion.div
              key="message-square"
              initial={{ rotateY: -90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: 90, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="flex items-center justify-center"
            >
              <MessageSquare size={20} strokeWidth={2} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* AI Agent text - hidden on mobile, visible on larger screens */}
      <span className="hidden sm:inline relative z-10 text-xs font-medium mt-0.5">
        AI Agent
      </span>
    </motion.button>
  );
};

export default MobileAIAgentButton; 